import { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import SearchField from '../common/SearchField';
import { useGetLowStockPageMutation } from '../../api/reports/ReportApi';
import { LowStockRow } from '../../types/reports';
import { exportToExcel } from '../../utils/exportExcel';

const centred = { flex: 1, align: 'center', headerAlign: 'center' } as const;

/**
 * What to order, before a customer asks for it.
 *
 * <p>Each product carries its own level, because "low" is not one number for a whole
 * shop - a screen guard that sells forty a week is already losing business at three,
 * while a premium case at two is perfectly normal. The box at the top is the fallback
 * for products where nobody has set one.
 *
 * <p>Paged from the server like every other list in the app: a shop with five hundred
 * products should not be handed all of them to draw.
 */
const StockAlertReport = () => {
  const [searchValue, setSearchValue] = useState('');
  const [levelInput, setLevelInput] = useState('3');
  const [rows, setRows] = useState<LowStockRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [getLowStockPage] = useGetLowStockPageMutation();

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getLowStockPage({
          searchValue,
          level: Number(levelInput) || 0,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, levelInput, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, levelInput]);

  const columns: GridColDef[] = [
    { field: 'productName', headerName: 'Product', ...centred, minWidth: 220, align: 'left', headerAlign: 'left' },
    { field: 'brandName', headerName: 'Brand', ...centred, minWidth: 120 },
    { field: 'modelName', headerName: 'Model', ...centred, minWidth: 110 },
    {
      field: 'serialTracked',
      headerName: 'Tracked by',
      ...centred,
      minWidth: 140,
      renderCell: (row) => (row.value ? 'Serial number' : 'Quantity'),
    },
    {
      field: 'availableStock',
      headerName: 'Available',
      ...centred,
      minWidth: 100,
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={700} color={Number(row.value) === 0 ? 'error.main' : 'warning.main'}>
          {String(row.value)}
        </Typography>
      ),
    },
    {
      field: 'warnAt',
      headerName: 'Warning level',
      ...centred,
      minWidth: 120,
      renderCell: (row) => (
        <Typography variant="body2">
          {String(row.value)}
          <Typography component="span" variant="caption" color="text.secondary">
            {row.row.reorderLevel == null ? ' (default)' : ' (this product)'}
          </Typography>
        </Typography>
      ),
    },
    { field: 'sellingPrice', headerName: 'Price', ...centred, minWidth: 100 },
    {
      field: 'status',
      headerName: 'Status',
      ...centred,
      minWidth: 130,
      renderCell: (row) => (
        <Chip
          size="small"
          color={row.value === 'OUT_OF_STOCK' ? 'error' : 'warning'}
          variant={row.value === 'OUT_OF_STOCK' ? 'filled' : 'outlined'}
          label={row.value === 'OUT_OF_STOCK' ? 'Out of stock' : 'Low'}
        />
      ),
    },
  ];

  /** The export is the page on screen - the same rows the shop is looking at. */
  const download = () =>
    exportToExcel(
      rows.map((row) => ({
        Product: row.productName,
        Brand: row.brandName ?? '',
        Model: row.modelName ?? '',
        Available: row.availableStock,
        'Warn at': row.warnAt,
        Status: row.status === 'OUT_OF_STOCK' ? 'Out of stock' : 'Low',
        'Selling Price': row.sellingPrice ?? '',
      })),
      `low-stock-level-${levelInput}`,
    );

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder="Any words: brand, model, colour, RAM, storage, barcode..." width={300} />
        <TextField
          label="Warn at (default)"
          size="small"
          type="number"
          sx={{ width: 175 }}
          helperText="Used when a product has none of its own"
          value={levelInput}
          onChange={(event) => setLevelInput(event.target.value)}
        />
        <Chip variant="outlined" label={`${rowCount} product(s) to order`} color={rowCount > 0 ? 'warning' : 'default'} />
        <Box flexGrow={1} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} disabled={rows.length === 0}>
          Excel
        </Button>
      </Stack>

      <DataGrid
        columns={columns}
        rows={rows}
        getRowId={(row) => row.productId}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        loading={loading}
        sx={{ height: '65vh' }}
      />

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        A product is listed when its stock reaches its warning level. Set that level per product on the product form
        (Products, edit, Reorder Level); products without one use the default in the box above. Out of stock is listed
        first - those are the sales being lost today.
      </Typography>
    </Box>
  );
};

export default StockAlertReport;
